import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
import { UIRouterReact } from '@uirouter/react';
import { createRouterMiddleware, routerReducer } from '@uirouter/redux';
import thunk from 'redux-thunk';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import rootReducer from '../checkout/reducers/index';
import InitRollbar from '@c/InitRollbar';
import reduxDevToolsComposeOptions from '../listing/helpers/reduxDevtoolsConfig';
import { getOrderTotal } from "../checkout/selectors/cart";
import { totalCostChange } from "../checkout/actions/ui";
import { getTopWindow } from '@h/window';
import { isAuthorizedAsUser, isAuthorizedAsGuest, isAuthStarted } from "@/selectors/auth";
import { isListingCheckoutLoginEnabled } from "@/selectors/settings";
import { applyCartedTicketsToQuantitiesForAllLevelsInTicketPicker } from '../checkout/views/ticket-picker/actions/ticket-picker';

const setupApp = (brandName, actionCallback = null, persistSuffix = '') => {

    InitRollbar(brandName);
    const router = new UIRouterReact();
    // match with or without trailing slash
    // https://ui-router.github.io/react/docs/0.8.9/classes/url.urlconfig.html#strictmode
    router.urlService.config.strictMode(false);
    const routerMiddleware = createRouterMiddleware(router);

    // @ts-ignore
    const reduxDevConfig = {
        ...reduxDevToolsComposeOptions,
        name: `checkout_${new Date().getTime().toString()}`,
    };

    // if we are in an iframe, attach to the top window
    let windowToAttachTo = getTopWindow();

    const composeEnhancers = windowToAttachTo.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? windowToAttachTo.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__(reduxDevConfig) : compose;

    const compareTotalCost = (oldValue, newValue) =>
        JSON.stringify(oldValue) === JSON.stringify(newValue);

    const dispatchTotalCostChangeMiddleware = getTotal => store => next => action => {
        const oldTotal = getTotal(store.getState());
        let result = next(action);
        const newTotal = getTotal(store.getState());
        if (!compareTotalCost(oldTotal, newTotal)) {
            store.dispatch(totalCostChange(oldTotal, newTotal));
        }

        return result;
    };

    const authMiddleware = store => next => action => {
      const state = store.getState();
      const authEnabled = isListingCheckoutLoginEnabled(state);
      const authStarted = isAuthStarted(state);
      const isAuthorized = isAuthorizedAsUser(state) || isAuthorizedAsGuest(state);
      const routerName = router.stateService.current.name;

      if (!routerName) {
        return next(action);
      }

      // Transfer to auth if authentication has started and the user tries to access a different page
      if (authEnabled && authStarted && !isAuthorized
        && (!["auth", "authEmail"].includes(routerName))) {
        router.stateService.go('auth');
      }

      // Transfer to auth page on checkout
      if (authEnabled && !(authStarted || isAuthorized) && routerName == 'checkout') {
        router.stateService.go('auth');
      }

      return next(action);
    }
    const applyTicketsToTicketPickerQuantityArraysMiddleware = store => next => action => {
        const result = next(action);
        /**
         * These actions set quantity arrays in the ticket picker. After processing them,
         * dispatch an additional action to apply the carted tickets to those quantity
         * arrays so that the quantities in the ticket picker reflect the already carted
         * tickets.
         */
        const watchActions = ['FETCH_EVENT', 'REFRESH_EVENT', 'SET_PRICE_LEVELS', 'UPDATE_PRICE_LEVELS'];
        if (watchActions.includes(action.type)) {
            store.dispatch(applyCartedTicketsToQuantitiesForAllLevelsInTicketPicker());
        }
        return result;
    }

    // general purpose middleware for subscribing to dispatched actions
    const actionCallbackMiddleware = store => next => action => {
        const result = next(action);
        if (typeof actionCallback === 'function') {
            actionCallback(action, store);
        }
        return result;
    }

    // config for persisting reducer state -- storage is LocalStorage for web
    const persistConfig = {
        key: 'root' + persistSuffix,
        storage: storage,
        /**
         * IMPORTANT: Reducers in this list should have a case for RECONCILE_PERSISTED_STATE
         * The exception is reducers that use createSlice from '@reduxjs/toolkit' as they
         * seem to handle properties missing from default defined state
         */
        whitelist: ['member', 'delivery', 'coupon', 'ui', 'fees', 'dynamicListing', 'auth']
    };

    // all of our reducers need to be wrapped by persistReducer even though we only actually persist based on the whitelist
    const persistedReducer = persistReducer(persistConfig, combineReducers({
        ...rootReducer,
        router: routerReducer
    }));

    // create store as normal with persisted reducers
    const store = createStore(
        persistedReducer,
        composeEnhancers(
            applyMiddleware(
                thunk,
                routerMiddleware,
                authMiddleware,
                dispatchTotalCostChangeMiddleware(getOrderTotal),
                applyTicketsToTicketPickerQuantityArraysMiddleware,
                actionCallbackMiddleware
            )
        )
    );

    let reconciledState = false;

    // persist the store
    const persistor = persistStore(store, null, () => {
        // add any missing fields from default state to the rehydrated state
        if (!reconciledState) {
            store.dispatch({type: 'RECONCILE_PERSISTED_STATE'});
            reconciledState = true;
        }
    });

    return {
        router,
        persistor,
        store,
    };
}

export default setupApp;
